import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { sortPhaseAppointments } from '../../../../../../../selectors/appointments';
import moment from 'moment';
import { Button } from 'react-toolbox';
import ReactTooltip from 'react-tooltip';
import ColoredButton from 'components/ColoredButton';
import { can } from '../../../../../../../services';
import Card from 'components/Card';
import { ResourceCardElement } from 'components/ResourceCardElements';
import { navigate, requestPhaseAppointments } from 'actionCreators';
import { urls } from 'helpers/url';
import { RoleType } from 'types/user';
import { AppointmentType } from 'types/appointment';
import DeleteAppointmentModal from './components/deleteAppointmentModal';
import ConvertAppointmentModal from './components/convertAppointmentModal';
import PrintInstallerPaperworkModal from '../../components/printInstallerPaperworkModal';
import { PHASE_APPOINTMENT_TYPES, ADD_APPT_TYPE_LABELS } from 'config/constants';
import { isDeliveryPhase, titleize } from '../../../../../../../utils';
import S from './phaseAppointmentCard.scss';
import cardStyles from 'styles/resourceCard.scss';
import styles from './styles.scss';

class PhaseAppointments extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      errors: {},
      deleteAppointment: false,
      convertAppointment: false,
      editAppointment: false,
      printAppointment: false,
    };
  }

  componentDidMount = () => this.props.requestPhaseAppointments(this.props.phaseId);

  can = (ability, attrs = {}) => can(this.props.role, ability, 'appointment_form', attrs)

  isCcs = () => this.props.position === 'Customer Care Specialist';

  canEditApptType = type => !this.isCcs() ||
    ['installation', 'installation_hold', 'delivery', 'delivery_hold', 'assessment', 'assessment_hold'].includes(type);

  sortAppointments = (appointments) => {
    const scheduled = [];
    const completed = [];
    appointments.forEach((appt) => {
      if (appt.end_moment.isSameOrAfter(moment().startOf('day'))) scheduled.push(appt);
      else completed.push(appt);
    });
    return { scheduled, completed };
  }

  handleAddAppointment = apptType => this.props.navigate(urls.newAppointment(
    this.props.phaseId, apptType === 'installation_hold' ? 'installation' : apptType,
  ));

  handleAddAssessment = () => (this.props.role === 'estimator' ?
    this.props.navigate(urls.newAppointment(this.props.phaseId, 'assessment')) :
    this.props.navigate(`/phases/${this.props.phaseId}/scheduling-checklist`));

  handleDeleteAppointment = appt => this.setState({
    selectedAppointment: appt, deleteAppointment: true,
  });

  handleConvertAppointment = appt => this.setState({
    selectedAppointment: appt, convertAppointment: true,
  });

  handleDeleteAppointmentClose = () => this.setState({ deleteAppointment: false },
    () => window.location.reload());

  handleConvertAppointmentClose = () => this.setState({ convertAppointment: false },
    () => window.location.reload());

  handleEditAppointment = appt =>
    this.props.navigate(`/phases/${this.props.phaseId}/appointments/${appt.id}/edit/${appt.kind}`);

  handlePrintAppointment = appt => this.setState({
    selectedAppointment: appt, printAppointment: true,
  });

  handlePrintAppointmentClose = () => this.setState({ printAppointment: false });

  handleEditAppointmentClose = () => this.setState({ editAppointment: false });

  handleDisableEditDelete = (apptId, apptPhaseId, date, phaseType, apptType = 'edit') => {
    if ((this.isCcs() && phaseType !== 'Assessment' && apptType === 'edit') ||
        moment().isAfter(date, 'day') ||
        apptPhaseId !== this.props.phaseId) return true;
    const laborSheet = this.props.laborSheets.filter(ls => ls.appointment)
      .find(ls => ls.appointment.id === apptId) || null;
    if (laborSheet) return (['Ready for Accounting', 'Recorded'].includes(laborSheet.status));
    return false;
  }

  phaseBlocked = () => ['canceled'].includes(this.props.phaseStatus);

  ifiHiddenApptTypes = () => {
    if (this.props.phaseSource !== 'IFI') return [];
    const { phaseType } = this.props;
    if (phaseType === 'Installation') return ['stack_cali_bamboo', 'deliver_stack_cali_bamboo', 'delivery', 'delivery_hold', 'bust_day'];
    if (phaseType === 'Workorder') return ['stack_cali_bamboo', 'bust_day'];
    return [];
  };

  determineAddBtns = () => {
    if (this.phaseBlocked()) return this.renderPhaseBlocked();
    const { storeSchedulable, phaseSchedulable, ccsSchedulable,
      phaseType, imeSurface } = this.props;
    const blockArr = [];
    if (this.isCcs()) {
      if (!storeSchedulable) blockArr.push('Store not CCS Schedulable');
      if (!phaseSchedulable || imeSurface) blockArr.push('Phase not CCS Schedulable');
    }
    let btns = null;
    if (!this.isCcs() || (!imeSurface && phaseType === 'Assessment')) btns = this.renderAddApptBtns(blockArr);
    else if (!imeSurface && ccsSchedulable) btns = this.renderCcsAddApptBtns();
    else if (phaseType !== 'Assessment') btns = this.renderCcsCannotSchedule(blockArr);
    return btns;
  };

  determineRenderMessage = () => {
    const { ccsSchedulable, appointments } = this.props;
    if (this.isCcs() && !ccsSchedulable && appointments.length === 0) {
      return this.renderCcsNotSchedulable();
    } else if (appointments.length === 0) return this.renderNoAppointmentsMsg();
    return null;
  }

  determineDisableReason = (apptId, apptPhaseId, date, dateType) => {
    if (apptPhaseId !== this.props.phaseId) return 'appt. belongs to related phase';
    else if (moment().isAfter(date, 'day')) return `appt. ${dateType} in the past`;
    else if (this.isCcs()) return 'user is a CCS';
    const laborSheet = this.props.laborSheets.filter(ls => ls.appointment)
      .find(ls => ls.appointment.id === apptId) || null;
    if (laborSheet && ['Ready for Accounting', 'Recorded'].includes(laborSheet.status)) {
      return 'labor sheet status';
    }
    return 'N/A';
  }

  ifiApptAttrs = () => {
    const { phaseSource, phaseInstallerId, phaseEstimatorId,
            employableId, isSelfSchedulable } = this.props;
    return {
      source: phaseSource,
      phase_installer_id: phaseInstallerId,
      phase_estimator_id: phaseEstimatorId,
      employable_id: employableId,
      is_self_schedulable: isSelfSchedulable,
    };
  }

  renderPhaseBlocked = () => {
    const { phaseStatus, phaseType } = this.props;
    let additionalInfo = '';
    if (phaseType === 'Assessment') additionalInfo = ' Please add an estimator inquiry flag to have EC reopen.';
    if (phaseType === 'Installation') additionalInfo = ' If install is active in IMS override to scheduling to add appointment.';
    return (<Fragment>
      <div style={{ color: 'red' }}>Unable to add appointments to a {phaseStatus} phase.{additionalInfo}</div>
      <br />
    </Fragment>);
  };

  renderCcsCannotSchedule = blockArr => <Fragment>
    <div style={{ color: 'red' }}>CCS cannot schedule due to: {blockArr.join(', ')}</div>
    <br />
  </Fragment>;

  renderAssignee = (appt) => {
    let url = '';
    const nonCrewObject = { id: appt.assignee_id };
    switch (appt.assignee_type) {
      case 'Crew':
        url = urls.crew({ installer_id: appt.installer_id });
        break;
      case 'Estimator':
        url = urls.estimator(nonCrewObject);
        break;
      case 'IQM':
        url = urls.iqm(nonCrewObject);
        break;
      default:
        url = '';
    }
    return (<Link to={url}>{appt.assignee}</Link>);
  }

  renderCcsAddApptBtns = () => {
    const { phaseType, surfaceType, storeDeliveryMethod, spigDelivery } = this.props;
    const delivery = isDeliveryPhase(phaseType, surfaceType, storeDeliveryMethod, spigDelivery);
    return (phaseType === 'Installation' ? (<div>
      <ColoredButton
        color="blue"
        className={styles.addButton}
        label={`Add ${delivery ? 'Delivery & ' : ''}Installation/Hold`}
        onClick={() => this.handleAddAppointment('installation')}
      />
    </div>) : null);
  }

  renderAddApptBtns = blocks => (can(this.props.role, 'create', 'appointment', this.ifiApptAttrs()) ? (<div>
    {this.props.phaseType !== 'Assessment' && blocks.length > 0 && this.renderCcsCannotSchedule(blocks)}
    {this.props.phaseType === 'Assessment' ? (<Fragment>
      <ColoredButton
        key="assessment"
        color="blue"
        className={styles.addButton}
        label={ADD_APPT_TYPE_LABELS.assessment}
        onClick={() => this.handleAddAssessment()}
      />
      <ColoredButton
        key="assessment_hold"
        color="blue"
        className={styles.addButton}
        label={ADD_APPT_TYPE_LABELS.assessment_hold}
        onClick={() => this.handleAddAppointment('assessment_hold')}
      />
    </Fragment>) : (<Fragment>
      {PHASE_APPOINTMENT_TYPES[this.props.phaseType].filter(a => a !== 'installation_hold' && !this.ifiHiddenApptTypes().includes(a))
        .map(a => (<ColoredButton
          key={a}
          color="blue"
          className={styles.addButton}
          label={ADD_APPT_TYPE_LABELS[a]}
          onClick={() => this.handleAddAppointment(a)}
        />))}
    </Fragment>)}
  </div>) : null);

  renderEditDeleteDisableTooltip = (apptId, apptPhaseId, date, dateType = 'start') =>
    <ReactTooltip id={`${dateType === 'start' ? 'delete' : 'edit'}Action${apptId}`} place="top" effect="solid">
      <span className={S.tooltipText}>
        {`Action disabled - ${this.determineDisableReason(apptId, apptPhaseId, date, dateType)}`}
      </span>
    </ReactTooltip>

  renderAppointment = (appt, altColor, title) => {
    const { role, zone, user: { id, name }, phaseType } = this.props;
    const { id: apptId, start_moment: startMoment, end_moment: endMoment, start_date, end_date,
            phase_id: apptPhaseId, appointment_confirmation_moment, product_confirmation_moment,
            pickup_moment, assignee, user_id, kind, scheduling_reason } = appt;
    const dateStart = startMoment.format('MM/DD/YY');
    const dateEnd = end_date && end_date !== start_date ? endMoment.format('MM/DD/YY') : null;
    const timezone = zone.includes('Eastern') ? 'EST' : 'CST';
    const arrivalWindow = `${startMoment.format('hh:mma')} - ${endMoment.format('hh:mma')} ${timezone}`;
    const confirmation = appointment_confirmation_moment && appointment_confirmation_moment.local().format('MM/DD/YY').toString();
    const productConfirmation = product_confirmation_moment && product_confirmation_moment.local().format('MM/DD/YY').toString();
    const pickupDate = pickup_moment && pickup_moment.local().format('MM/DD/YY').toString();
    const canAttrs = role === 'iqm' ? { user_name: name, obj_name: assignee } : { user_id: id, obj_id: user_id };
    const showConvert = !this.phaseBlocked() && !title.includes('previous') && phaseType === 'Installation' && (['installation_hold', 'delivery_hold'].includes(kind));
    const showWoConvert = !this.phaseBlocked() && !title.includes('previous') && phaseType === 'Workorder' && (['workorder_hold'].includes(kind));
    const showAssessmentConvert = !this.phaseBlocked() && !title.includes('previous') && phaseType === 'Assessment' && kind === 'assessment_hold';
    const editDisabled = this.phaseBlocked() ||
      this.handleDisableEditDelete(apptId, apptPhaseId, endMoment, phaseType);
    const deleteDisabled = this.phaseBlocked() || this.handleDisableEditDelete(apptId, apptPhaseId, startMoment, phaseType, 'delete');
    return (<Card key={apptId} id={`appointment${apptId}`} className={cardStyles.resourceCard} altColor={altColor}>
      <ResourceCardElement
        title="Appointment Type"
        value={kind === 'repair_inspection' ? 'Workorder Inspection' : titleize(kind)}
        size="medium"
        theme={S}
      />
      <ResourceCardElement
        title="Date"
        value={dateStart + (dateEnd ? ` - ${dateEnd}` : '')}
        size="medium"
        theme={S}
      />
      <ResourceCardElement
        title="Arrival Window"
        value={arrivalWindow}
        size="medium"
        theme={S}
      />
      <ResourceCardElement
        title="Assigned To"
        value={this.renderAssignee(appt)}
        size="medium"
        theme={S}
      />
      <ResourceCardElement
        title="Scheduling Reason"
        value={scheduling_reason || 'N/A'}
        size="medium"
        theme={S}
      />
      <ResourceCardElement
        title="Appt. Confirmation"
        value={confirmation || 'N/A'}
        size="medium"
        theme={S}
      />
      <ResourceCardElement
        title="Product Confirmation"
        value={productConfirmation || 'N/A'}
        size="medium"
        theme={S}
      />
      {pickupDate && <ResourceCardElement
        title="Pickup"
        value={pickupDate}
        size="medium"
        theme={S}
      />}
      <ResourceCardElement size="full">
        {kind !== 'delivery_hold' ? showConvert && <Fragment>
          <div
            className={styles.editAndDeleteContainer}
            data-tip data-tip-disable={!showConvert} data-for={`convertInstall${apptId}`}
          >
            <Button
              icon="done"
              onClick={() => this.handleConvertAppointment(appt)}
              className={styles.button}
              mini inverse flat ripple
            />
          </div>
        </Fragment> : showConvert && <Fragment>
          <div
            className={styles.editAndDeleteContainer}
            data-tip data-tip-disable={!showConvert} data-for={`convertDelivery${apptId}`}
          >
            <Button
              icon="done"
              onClick={() => this.handleConvertAppointment(appt)}
              className={styles.button}
              mini inverse flat ripple
            />
          </div>
        </Fragment>}
        {showWoConvert && <div
          className={styles.editAndDeleteContainer}
          data-tip data-tip-disable={!showWoConvert} data-for={`convertWoConvert${apptId}`}
        >
          <Button
            icon="done"
            onClick={() => this.handleConvertAppointment(appt)}
            className={styles.button}
            mini inverse flat ripple
          />
        </div>}
        <ReactTooltip id={`convertInstall${apptId}`} place="top" effect="solid">
          <span className={S.tooltipText}>Confirm Installation</span>
        </ReactTooltip>
        <ReactTooltip id={`convertDelivery${apptId}`} place="top" effect="solid">
          <span className={S.tooltipText}>Confirm Delivery</span>
        </ReactTooltip>
        <ReactTooltip id={`convertWoConvert${apptId}`} place="top" effect="solid">
          <span className={S.tooltipText}>Confirm Workorder</span>
        </ReactTooltip>
        {showAssessmentConvert && <div
          className={styles.editAndDeleteContainer}
          data-tip data-tip-disable={!showAssessmentConvert} data-for={`convertAssessment${apptId}`}
        >
          <Button
            icon="done"
            onClick={() => this.handleConvertAppointment(appt)}
            className={styles.button}
            mini inverse flat ripple
          />
        </div>}
        <ReactTooltip id={`convertAssessment${apptId}`} place="top" effect="solid">
          <span className={S.tooltipText}>Confirm Assessment</span>
        </ReactTooltip>
        {this.can('delete', canAttrs) && this.canEditApptType(kind) && <div
          className={styles.editAndDeleteContainer}
          data-tip data-tip-disable={!deleteDisabled} data-for={`deleteAction${apptId}`}
        >
          <Button
            icon="delete"
            onClick={() => this.handleDeleteAppointment(appt)}
            className={`${styles.button} ${styles.tooltipBtn}`}
            mini inverse flat ripple
            disabled={deleteDisabled}
          />
        </div>}
        {this.can('update', canAttrs) && this.canEditApptType(kind) && <div
          className={styles.editAndDeleteContainer}
          data-tip data-tip-disable={!editDisabled} data-for={`editAction${apptId}`}
        >
          <Button
            icon="mode_edit"
            onClick={() => this.handleEditAppointment(appt)}
            className={`${styles.button} ${styles.tooltipBtn}`}
            mini inverse flat ripple
            disabled={editDisabled}
          />
        </div>}
        {this.renderEditDeleteDisableTooltip(apptId, apptPhaseId, endMoment, 'end')}
        {this.renderEditDeleteDisableTooltip(apptId, apptPhaseId, startMoment)}
        <Button
          icon="print"
          onClick={() => this.handlePrintAppointment(appt)}
          className={styles.button}
          mini inverse flat ripple
        />
      </ResourceCardElement>
    </Card>);
  }

  renderAppointmentList = (title, appointments) => (<Card
    className={S.phaseAppointmentCard__container}
  >
    <section className={S.phaseAppointmentCard}>
      <div className={S.phaseAppointmentCard__title}>
        <span>{title}</span>
      </div>
      <div className={S.phaseAppointmentCard__content}>
        {appointments.map((appt, i) => this.renderAppointment(appt, i % 2 !== 0, title))}
      </div>
    </section>
  </Card>);

  renderNoAppointmentsMsg = () => (<Card className={styles.msgCard}>
    <h4>Use the button(s) above to add appointments</h4>
  </Card>);

  renderCcsNotSchedulable = () => (<Card className={styles.msgCard}>
    <h4>Unable to add appointment(s) for this phase</h4>
  </Card>);

  render = () => {
    const { phaseId, phaseSource, appointments, role, workroom } = this.props;
    const { selectedAppointment, deleteAppointment,
            convertAppointment, printAppointment } = this.state;
    const { scheduled, completed } = this.sortAppointments(appointments);
    const scheduledAppointments = this.renderAppointmentList('scheduled', scheduled);
    const completedAppointments = this.renderAppointmentList('previous appointments', completed);
    return (<section>
      {this.determineAddBtns()}
      {this.determineRenderMessage()}
      {scheduled.length > 0 && scheduledAppointments}
      {completed.length > 0 && completedAppointments}
      <ConvertAppointmentModal
        phaseId={phaseId}
        appointment={selectedAppointment}
        active={convertAppointment}
        onClose={this.handleConvertAppointmentClose}
      />
      <DeleteAppointmentModal
        appointment={selectedAppointment}
        active={deleteAppointment}
        onClose={this.handleDeleteAppointmentClose}
        phaseSource={phaseSource}
        phaseWorkroom={workroom}
      />
      <PrintInstallerPaperworkModal
        appointment={selectedAppointment}
        active={printAppointment}
        onClose={this.handlePrintAppointmentClose}
        role={role}
      />
    </section>);
  }
}

PhaseAppointments.propTypes = {
  phaseId: PropTypes.number.isRequired,
  phaseType: PropTypes.string,
  phaseStatus: PropTypes.string,
  phaseSource: PropTypes.string,
  phaseInstallerId: PropTypes.number,
  phaseEstimatorId: PropTypes.number,
  employableId: PropTypes.number,
  isSelfSchedulable: PropTypes.bool,
  surfaceType: PropTypes.string,
  workroom: PropTypes.string,
  zone: PropTypes.string,
  ccsSchedulable: PropTypes.bool.isRequired,
  storeSchedulable: PropTypes.bool,
  phaseSchedulable: PropTypes.bool,
  appointments: PropTypes.arrayOf(AppointmentType).isRequired,
  role: PropTypes.string.isRequired,
  requestPhaseAppointments: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  position: PropTypes.string,
  user: RoleType,
  laborSheets: PropTypes.array,
  storeDeliveryMethod: PropTypes.string,
  spigDelivery: PropTypes.bool,
  imeSurface: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const { phase: { details: { id, type, ccs_schedulable: phaseSchedulable, status, source,
    installer_id: phaseInstallerId, estimator_id: phaseEstimatorId,
    workroom: { name, timezone }, surface_type: { name: surfaceType },
    store: { delivery_method, ccs_schedulable: storeSchedulable }, spig_delivery: spigDelivery },
    laborSheets = {} }, user: { role, position, employable_id: employableId,
    is_self_schedulable: isSelfSchedulable }, user } = state;
  const imeSurface = surfaceType === 'Pro Direct';
  return {
    phaseId: id,
    phaseType: type,
    phaseStatus: status,
    phaseSource: source,
    phaseInstallerId,
    phaseEstimatorId,
    employableId,
    isSelfSchedulable: !!isSelfSchedulable,
    surfaceType,
    storeDeliveryMethod: delivery_method,
    ccsSchedulable: storeSchedulable && phaseSchedulable,
    storeSchedulable,
    phaseSchedulable,
    imeSurface,
    spigDelivery,
    workroom: name,
    zone: timezone,
    appointments: sortPhaseAppointments(state),
    position,
    role,
    user,
    laborSheets: laborSheets.items || [],
  };
};

export default connect(
  mapStateToProps, { navigate, requestPhaseAppointments },
)(PhaseAppointments);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseAppointments/index.jsx